import { Injectable, inject } from '@angular/core';
import { Observable, Subject, of } from 'rxjs';
import { map, shareReplay, take, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { MultiTranslateHttpLoader } from '../common/multi-translate-http-loader/multi-translate-http-loader';
import * as LanguageFunctions from 'Shared/app/core/helpers/language.helper';
import { StaticForm, StaticForms } from '../models/static-forms';
import { Languages, TextDirection } from 'Shared/app/constants/languages.constants';

@Injectable()
export class StaticFormLanguageService {
  readonly #translateService = inject(TranslateService);
  readonly #httpLoader = inject(MultiTranslateHttpLoader);
  readonly defaultLanguage = { resource: Languages.Swedish.resource, name: Languages.Swedish.name, code: Languages.Swedish.code };

  #templateDirection: TextDirection = TextDirection.LTR;
  #initialized = false;
  #requestedLanguage = this.defaultLanguage.resource;

  readonly directionSubject = new Subject<TextDirection>();
  readonly direction$ = this.directionSubject.asObservable();

  initialize(
    formType: string,
    formVersion = 1,
    languagesCodes: number[] = [this.defaultLanguage.code],
    initialLanguage = this.defaultLanguage.resource
  ): Observable<void> {
    this.setFormNamePath(formType, formVersion);
    this.setSupportedLanguages(languagesCodes, initialLanguage);
    const effectiveLanguage = this.#getEffectiveLanguage(initialLanguage);
    const loaded$ = this.#loadFormTranslations(effectiveLanguage);
    this.setTemplateDirection(effectiveLanguage);
    this.#initialized = true;
    return loaded$;
  }

  getInitialized(): boolean {
    return this.#initialized;
  }

  resetLanguages(): void {
    this.#httpLoader.setFormPath(null);
    this.#httpLoader.setSupportedLanguages([this.defaultLanguage.resource]);
    // Hard reset of translation service
    (this.#translateService as any)._translationRequests = {};
    this.#initialized = false;
    this.#translateService.use(this.#requestedLanguage);
  }

  getTemplateDirection(): TextDirection {
    return this.#templateDirection;
  }

  // Set form file path and setup the translation loader
  setFormNamePath(formType: string, formVersion: number): void {
    const form = (Object.values(StaticForms) as StaticForm[]).find(
      (form) => form.typeName === formType && form.latestVersion === formVersion
    );

    if(!form) {
      return;
    }

    this.#httpLoader.setFormPath(form.langPath + '/' + formVersion.toFixed(1));
  }

  setSupportedLanguages(
    languagesCodes: number[] = [this.defaultLanguage.code],
    language: string = this.defaultLanguage.resource
  ) {
    const languageResources = LanguageFunctions.getLanguageResources(languagesCodes);
    const supportedLanguages = languageResources.includes(this.defaultLanguage.resource)
      ? languageResources
      : [this.defaultLanguage.resource, ...languageResources];
    this.#httpLoader.setSupportedLanguages(supportedLanguages);
    this.#translateService.addLangs(supportedLanguages);
    this.setLanguage(language);
  }

  setTemplateDirection(lang: string = this.defaultLanguage.name): void {
    const isRTL = this.isRTL(this.#getEffectiveLanguage(lang));
    if (this.#templateDirection === (isRTL ? TextDirection.RTL : TextDirection.LTR)) {
      return;
    }
    this.#templateDirection = isRTL ? TextDirection.RTL : TextDirection.LTR;
    this.directionSubject.next(this.#templateDirection);
  }

  setLanguage(lang: string, keepFallbackLanguage = false): void {
    const supportedLanguages = this.#httpLoader.getSupportedLanguages();
    this.#requestedLanguage = lang || this.defaultLanguage.resource;
    const effectiveLanguage = this.#getEffectiveLanguage(this.#requestedLanguage);

    const defaultLang =
        effectiveLanguage !== this.defaultLanguage.resource && effectiveLanguage !== Languages.English.resource && supportedLanguages.includes(Languages.English.resource)
        ? Languages.English.resource
        : this.defaultLanguage.resource;

    if (!keepFallbackLanguage) {
      this.#translateService.setFallbackLang(defaultLang);
    }
    this.#translateService.use(effectiveLanguage);
    if (this.#initialized) {
      this.#loadFormTranslations(effectiveLanguage);
    }
  }

  // Forcibly override the fallback language, used for result page that only exists in swedish
  overrideFallbackLanguage(): Observable<void> {
    this.#translateService.setFallbackLang(this.defaultLanguage.resource);
    // Without a form path the loader never emits, so callers must not wait for it.
    if (!this.#httpLoader.hasFormPath()) {
      return of(undefined);
    }
    return this.#loadFormTranslations(this.defaultLanguage.resource);
  }

  isRTL(lang: string): boolean {
    if (!this.#httpLoader.getSupportedLanguages()?.includes(lang)) {
      return false;
    }
    return LanguageFunctions.getLanguageObjectByResource(lang)?.rtl ?? false;
  }

  #loadFormTranslations(language: string): Observable<void> {
    const effectiveLanguage = this.#getEffectiveLanguage(language);
    const loaded$ = this.#httpLoader.getTranslation(effectiveLanguage).pipe(
      take(1),
      tap((translations) => this.#translateService.setTranslation(effectiveLanguage, translations, true)),
      map(() => undefined),
      shareReplay(1)
    );
    
    loaded$.subscribe();
    return loaded$;
  }

  #getEffectiveLanguage(language: string): string {
    const normalizedLanguage = language?.toLowerCase();
    return this.#httpLoader
      .getSupportedLanguages()
      .find((supportedLanguage) => supportedLanguage.toLowerCase() === normalizedLanguage)
      ?? this.defaultLanguage.resource;
  }
}
